import React, { useState, useEffect, useCallback, useContext } from "react";
import { adminApiRequest } from "../utils/adminApi";
import { CalendarContext } from "../utils/CalendarContext";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function toYMD(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function parseLocalDate(str) {
  if (!str) return null;
  const [y, m, d] = str.split(/[-/T]/);
  return new Date(Number(y), Number(m) - 1, Number(d));
}

/** Returns the last Sunday (day=0) of the given month/year as a YYYY-MM-DD string */
function getLastSundayOfMonth(year, month) {
  // Start from the last day of the month and walk back to Sunday
  const lastDay = new Date(year, month + 1, 0); // last day of month
  const offset = lastDay.getDay(); // 0=Sun … 6=Sat
  lastDay.setDate(lastDay.getDate() - offset);
  return toYMD(lastDay);
}

function fmt12(timeStr) {
  if (!timeStr) return "";
  const [h, m] = timeStr.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, "0")} ${ampm}`;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function EventPill({ type, label, onClick }) {
  const colors = {
    hall: "bg-purple-600 text-white shadow-sm hover:brightness-110",
    task: "bg-amber-600 text-white shadow-sm hover:brightness-110",
    custom: "bg-amber-600 text-white shadow-sm hover:brightness-110",
    buffet: "bg-teal-600 text-white shadow-sm hover:brightness-110",
  };
  return (
    <span
      onClick={(e) => {
        if (onClick) {
          e.stopPropagation();
          onClick();
        }
      }}
      className={`inline-block max-w-full truncate rounded px-2 py-0.5 text-[10px] font-bold leading-tight transition-all ${colors[type] || "bg-slate-500 text-white"} ${onClick ? "cursor-pointer" : ""}`}
    >
      {label}
    </span>
  );
}

function DeleteConfirmModal({ onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onCancel}
      />
      <div className="relative z-10 w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100 mb-4">
          <i className="fa fa-trash text-xl text-red-500" />
        </div>
        <h3 className="text-lg font-bold text-slate-900 mb-2">Remove Event?</h3>
        <p className="text-sm text-slate-500 mb-6">
          Are you sure you want to remove event from calendar?
        </p>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 rounded-xl bg-slate-100 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-200 transition"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 rounded-xl bg-red-500 py-2.5 text-sm font-semibold text-white hover:bg-red-600 shadow-sm transition"
          >
            Remove
          </button>
        </div>
      </div>
    </div>
  );
}

function AddEventModal({
  dateStr,
  dateLabel,
  onAdd,
  onClose,
  initialData = null,
}) {
  const [title, setTitle] = useState(initialData?.title || "");
  const [startTime, setStart] = useState(initialData?.time || "");
  const [endTime, setEnd] = useState(initialData?.endTime || "");
  const [desc, setDesc] = useState(initialData?.description || "");
  const [type, setType] = useState(initialData?.type || "task");

  // Hall Rental specific details
  const [fullName, setFullName] = useState(initialData?.fullName || "");
  const [contact, setContact] = useState(initialData?.contact || "");
  const [email, setEmail] = useState(initialData?.email || "");
  const [guests, setGuests] = useState(initialData?.guests || "");
  const [streetAddress, setStreet] = useState(initialData?.streetAddress || "");
  const [city, setCity] = useState(initialData?.city || "");
  const [postcode, setPostcode] = useState(initialData?.postcode || "");
  const [country, setCountry] = useState(initialData?.country || "");
  const [specialReqs, setSpecialReqs] = useState(
    initialData?.specialRequirements || "",
  );
  const [charityEvent, setCharity] = useState(
    initialData?.charityEvent || "No",
  );
  const [occasion, setOccasion] = useState(initialData?.occasion || "");
  const [notes, setNotes] = useState(initialData?.notes || "");
  const [agreedTerms, setAgreed] = useState(initialData?.agreedTerms || false);
  const [timeError, setTimeError] = useState("");

  // Ensure state stays in sync if initialData changes (though modal usually unmounts)
  useEffect(() => {
    if (initialData) {
      setTitle(initialData.title || "");
      setStart(initialData.time || "");
      setEnd(initialData.endTime || "");
      setDesc(initialData.description || "");
      setType(initialData.type || "task");
      setFullName(initialData.fullName || "");
      setContact(initialData.contact || "");
      setEmail(initialData.email || "");
      setGuests(initialData.guests || "");
      setStreet(initialData.streetAddress || "");
      setCity(initialData.city || "");
      setPostcode(initialData.postcode || "");
      setCountry(initialData.country || "");
      setSpecialReqs(initialData.specialRequirements || "");
      setCharity(initialData.charityEvent || "No");
      setOccasion(initialData.occasion || "");
      setNotes(initialData.notes || "");
      setAgreed(initialData.agreedTerms || false);
    }
  }, [initialData]);

  const isEdit = !!initialData;

  // Determine if the selected date is today
  const todayStr = toYMD(new Date());
  const isToday = dateStr === todayStr;

  // Current time as "HH:MM" for the min attribute
  const nowHHMM = (() => {
    const n = new Date();
    return `${String(n.getHours()).padStart(2, "0")}:${String(n.getMinutes()).padStart(2, "0")}`;
  })();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isFormValid()) return;

    // If it's today and a start time is set, reject past times
    if (!isEdit && isToday && startTime && startTime < nowHHMM) {
      setTimeError("Start time cannot be in the past.");
      return;
    }
    setTimeError("");
    onAdd({
      id: initialData?.id || Date.now().toString(),
      title: title.trim(),
      time: startTime,
      endTime: endTime || null,
      description: desc.trim(),
      type: type,
      // Hall rental details
      fullName: type === "hall" ? fullName.trim() : null,
      contact: type === "hall" ? contact.trim() : null,
      email: type === "hall" ? email.trim() : null,
      guests: type === "hall" ? guests : null,
      streetAddress: type === "hall" ? streetAddress.trim() : null,
      city: type === "hall" ? city.trim() : null,
      postcode: type === "hall" ? postcode.trim() : null,
      country: type === "hall" ? country.trim() : null,
      specialRequirements: type === "hall" ? specialReqs.trim() : null,
      occasion: type === "hall" ? occasion.trim() : null,
      charityEvent: type === "hall" ? charityEvent : null,
      notes: type === "hall" ? notes.trim() : null,
      agreedTerms: type === "hall" ? agreedTerms : false,
    });
  };

  // Validation
  const isFormValid = () => {
    if (!title.trim() || !startTime || !endTime) return false;
    if (type === "hall") {
      return (
        fullName.trim() !== "" &&
        occasion.trim() !== "" &&
        contact.trim() !== "" &&
        email.trim() !== "" &&
        streetAddress.trim() !== "" &&
        city.trim() !== "" &&
        postcode.trim() !== "" &&
        country.trim() !== "" &&
        guests !== "" &&
        agreedTerms === true
      );
    }
    return true;
  };

  const modalColor = type === "hall" ? "bg-purple-600" : "bg-amber-500";
  const modalRing =
    type === "hall" ? "focus:ring-purple-400" : "focus:ring-amber-400";

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className={`relative z-10 w-full ${type === "hall" ? "max-w-md" : "max-w-sm"} rounded-2xl bg-white shadow-2xl overflow-hidden max-h-[90vh] flex flex-col transition-all duration-300`}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between ${modalColor} px-5 py-4 transition-colors duration-300`}
        >
          <div>
            <p className="text-xs opacity-80 font-semibold uppercase tracking-widest text-white">
              {isEdit ? "Edit Event" : "New Event"}
            </p>
            <h3 className="text-white font-bold text-base leading-tight mt-0.5">
              {dateLabel}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-white/80 hover:bg-black/10 hover:text-white transition"
          >
            &#x2715;
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4 overflow-y-auto">
          {/* Type Selector */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">
              Event Type
            </label>
            <div className="flex gap-2 p-1 bg-slate-100 rounded-xl">
              <button
                type="button"
                onClick={() => {
                  setType("task");
                  if (!isEdit && title === "Hall Rental") setTitle("");
                }}
                className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${type === "task" ? "bg-white text-amber-600 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
              >
                <i className="fa fa-tasks mr-1.5"></i> Task
              </button>
              <button
                type="button"
                onClick={() => {
                  setType("hall");
                  if (!isEdit && !title.trim()) setTitle("Hall Rental");
                }}
                className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${type === "hall" ? "bg-white text-purple-600 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
              >
                <i className="fa fa-building-o mr-1.5"></i> Hall Rental
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">
              Event Title{" "}
            </label>
            <input
              type="text"
              placeholder={
                type === "hall"
                  ? "e.g. Island Hall Rental"
                  : "e.g. Team meeting"
              }
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className={`w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 ${modalRing}`}
              required
              autoFocus
            />
          </div>

          {/* Hall Rental Details Section */}
          {type === "hall" && (
            <div className="space-y-6 border-y border-slate-100 py-4 my-2">
              {/* SECTION 1: CONTACT DETAILS */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className="h-1 w-8 bg-purple-500 rounded-full"></div>
                  <p className="text-[10px] font-bold text-slate-800 uppercase tracking-widest">
                    1. Contact Details
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">
                      Full Name
                    </label>
                    <input
                      type="text"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className={`w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 ${modalRing}`}
                      placeholder="John Doe"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">
                      Contact Number
                    </label>
                    <input
                      type="tel"
                      value={contact}
                      onChange={(e) => setContact(e.target.value)}
                      className={`w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 ${modalRing}`}
                      placeholder="07..."
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={`w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 ${modalRing}`}
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">
                    Street Address
                  </label>
                  <input
                    type="text"
                    value={streetAddress}
                    onChange={(e) => setStreet(e.target.value)}
                    className={`w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 ${modalRing}`}
                    placeholder="123 High St"
                  />
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">
                      City
                    </label>
                    <input
                      type="text"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      className={`w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 ${modalRing}`}
                      placeholder="Manchester"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">
                      Postcode
                    </label>
                    <input
                      type="text"
                      value={postcode}
                      onChange={(e) => setPostcode(e.target.value)}
                      className={`w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 ${modalRing}`}
                      placeholder="M1 1AA"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">
                      Country
                    </label>
                    <input
                      type="text"
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      className={`w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 ${modalRing}`}
                      placeholder="UK"
                    />
                  </div>
                </div>
              </div>

              {/* SECTION 2: BOOKING INFORMATION */}
              <div className="space-y-4 pt-4 border-t border-slate-100">
                <div className="flex items-center gap-2">
                  <div className="h-1 w-8 bg-purple-500 rounded-full"></div>
                  <p className="text-[10px] font-bold text-slate-800 uppercase tracking-widest">
                    2. Booking Details
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">
                      Occasion
                    </label>
                    <input
                      type="text"
                      value={occasion}
                      onChange={(e) => setOccasion(e.target.value)}
                      className={`w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 ${modalRing}`}
                      placeholder="e.g. Wedding"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">
                      Guests
                    </label>
                    <input
                      type="number"
                      value={guests}
                      onChange={(e) => setGuests(e.target.value)}
                      className={`w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 ${modalRing}`}
                      placeholder="50"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">
                      Start Time
                    </label>
                    <input
                      type="time"
                      value={startTime}
                      min={!isEdit && isToday ? nowHHMM : undefined}
                      onChange={(e) => {
                        setStart(e.target.value);
                        setTimeError("");
                      }}
                      className={`w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 ${modalRing}`}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">
                      End Time
                    </label>
                    <input
                      type="time"
                      value={endTime}
                      onChange={(e) => setEnd(e.target.value)}
                      className={`w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 ${modalRing}`}
                    />
                  </div>
                </div>

                <div className="flex items-center gap-4 bg-purple-50 p-3 rounded-xl border border-purple-100">
                  <label className="text-xs font-semibold text-purple-700">
                    Charity Event?
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-1.5 text-xs text-purple-700 cursor-pointer font-medium">
                      <input
                        type="radio"
                        name="charityEvent"
                        value="Yes"
                        checked={charityEvent === "Yes"}
                        onChange={(e) => setCharity(e.target.value)}
                        className="text-purple-600 focus:ring-purple-500"
                      />{" "}
                      Yes
                    </label>
                    <label className="flex items-center gap-1.5 text-xs text-purple-700 cursor-pointer font-medium">
                      <input
                        type="radio"
                        name="charityEvent"
                        value="No"
                        checked={charityEvent === "No"}
                        onChange={(e) => setCharity(e.target.value)}
                        className="text-purple-600 focus:ring-purple-500"
                      />{" "}
                      No
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">
                    Special Requirements
                  </label>
                  <textarea
                    value={specialReqs}
                    onChange={(e) => setSpecialReqs(e.target.value)}
                    className={`w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 ${modalRing} resize-none`}
                    placeholder="Catering, music, accessibility etc."
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">
                    Notes{" "}
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className={`w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 ${modalRing} resize-none`}
                    placeholder="Staff notes, payment status, etc."
                    rows={2}
                  />
                </div>
              </div>

              <div className="space-y-4 pt-2 border-t border-slate-100">
                <p className="text-[10px] font-bold text-purple-600 uppercase tracking-widest">
                  Agreement
                </p>

                <div className="flex flex-col gap-3">
                  <label className="flex items-start gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={agreedTerms}
                      onChange={(e) => setAgreed(e.target.checked)}
                      className="mt-0.5 rounded text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-xs text-slate-600 leading-tight">
                      Applicant has read and agreed to the Terms and Conditions
                    </span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Shared Details (only for non-hall types, since hall has them grouped above) */}
          {type !== "hall" && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">
                    Start Time
                  </label>
                  <input
                    type="time"
                    value={startTime}
                    min={!isEdit && isToday ? nowHHMM : undefined}
                    onChange={(e) => {
                      setStart(e.target.value);
                      setTimeError("");
                    }}
                    className={`w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 ${modalRing}`}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">
                    End Time
                  </label>
                  <input
                    type="time"
                    value={endTime}
                    onChange={(e) => setEnd(e.target.value)}
                    className={`w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 ${modalRing}`}
                  />
                </div>
              </div>

              {startTime && endTime && endTime < startTime && (
                <p className="text-xs font-medium text-emerald-600 -mt-2">
                  <i className="fa fa-moon-o mr-1"></i> Event spans into next
                  day
                </p>
              )}

              {timeError && (
                <p className="text-xs text-red-500 -mt-2">{timeError}</p>
              )}

              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  Description{" "}
                  <span className="font-normal text-slate-400">(optional)</span>
                </label>
                <textarea
                  placeholder="Add a note…"
                  value={desc}
                  onChange={(e) => setDesc(e.target.value)}
                  rows={3}
                  className={`w-full resize-none rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 ${modalRing}`}
                />
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-xl border border-slate-200 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!isFormValid()}
              className={`flex-1 rounded-xl py-2.5 text-sm font-bold text-white shadow-sm transition-all ${
                isFormValid()
                  ? type === "hall"
                    ? "bg-purple-600 hover:bg-purple-700 active:scale-95"
                    : "bg-amber-500 hover:bg-amber-600 active:scale-95"
                  : "bg-slate-300 cursor-not-allowed"
              }`}
            >
              {isEdit ? "Save Changes" : "Add Event"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Day Detail Panel ─────────────────────────────────────────────────────────

const TYPE_STYLE = {
  hall: {
    solid: "bg-purple-600",
    light: "bg-purple-100",
    text: "text-purple-800",
    border: "border-purple-400",
  },
  buffet: {
    solid: "bg-teal-600",
    light: "bg-teal-100",
    text: "text-teal-800",
    border: "border-teal-400",
  },
  task: {
    solid: "bg-amber-600",
    light: "bg-amber-100",
    text: "text-amber-800",
    border: "border-amber-400",
  },
  custom: {
    solid: "bg-amber-600",
    light: "bg-amber-100",
    text: "text-amber-800",
    border: "border-amber-400",
  },
};

const HOUR_PX = 64; // pixels per hour

function toMinutes(timeStr) {
  if (!timeStr) return null;
  const [h, m] = timeStr.split(":").map(Number);
  return h * 60 + (m || 0);
}

function DayPanel({
  dateStr,
  hallEvents,
  customEvents,
  isBuffetDay,
  onClose,
  onAddCustom,
  onRemoveCustom,
  onEditCustom,
  initialScrollMinutes,
}) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const date = parseLocalDate(dateStr);
  const label = date
    ? date.toLocaleDateString("en-GB", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : dateStr;

  // Determine if this date is strictly in the past (before today's date)
  const todayMidnight = new Date();
  todayMidnight.setHours(0, 0, 0, 0);
  const isPastDate = date ? date < todayMidnight : false;

  const handleAdd = (event) => {
    onAddCustom(event);
    setShowAddModal(false);
  };
  const handleEdit = (event) => {
    onEditCustom(event);
    setEditingEvent(null);
  };

  // ── Build event lists ────────────────────────────────────────────────────────
  const allDay = [];
  const timed = [];

  if (isBuffetDay) {
    allDay.push({
      id: "buffet",
      type: "buffet",
      title: "Buffet Day",
      subtitle: "Last Sunday of the month \u2014 buffet service",
      removable: false,
    });
  }
  hallEvents.forEach((r) => {
    const ev = {
      id: `hall-${r.id}`,
      type: "hall",
      title: r.full_name,
      subtitle: [r.event_type, r.guests ? `${r.guests} guests` : null]
        .filter(Boolean)
        .join(" \u00b7 "),
      time: r.start_time || null,
      endTime: r.end_time || null,
      drawStart: r.drawStart,
      drawEnd: r.drawEnd,
      spillNext: r.spillNext,
      spillPrev: r.spillPrev,
      removable: false,
      notes: r.notes || null,
      // Pass details for timeline header
      fullName: r.full_name,
      occasion: r.event_type,
      guests: r.guests,
    };
    (ev.time ? timed : allDay).push(ev);
  });
  customEvents.forEach((ev) => {
    const entry = {
      id: ev.id,
      type: ev.type || "task",
      title: ev.title,
      subtitle: (ev.type === "hall" ? ev.notes : ev.description) || null,
      time: ev.time || null,
      endTime: ev.endTime || null,
      drawStart: ev.drawStart,
      drawEnd: ev.drawEnd,
      spillNext: ev.spillNext,
      spillPrev: ev.spillPrev,
      removable: true,
      notes: ev.notes || null,
      raw: ev, // keep original for editing
      // Pass details for timeline header
      fullName: ev.fullName,
      occasion: ev.occasion,
      guests: ev.guests,
    };
    (entry.time ? timed : allDay).push(entry);
  });

  // Fixed 24-hour grid so panel scrolls from 12 AM to midnight
  const gridStartHour = 0;
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const totalHeight = 24 * HOUR_PX;

  const hasAny = allDay.length > 0 || timed.length > 0;

  // ── Auto-scroll logic ──────────────────────────────────────────────────────
  const bodyRef = React.useRef(null);
  React.useEffect(() => {
    if (!bodyRef.current) return;

    let scrollMin = null;

    // 1. If we have a specific requested scroll time from a pill click
    if (initialScrollMinutes !== null && initialScrollMinutes !== undefined) {
      scrollMin = initialScrollMinutes;
    }
    // 2. Otherwise fall back to earliest event of the day
    else if (timed.length > 0) {
      const validMins = timed
        .map((ev) => toMinutes(ev.drawStart || ev.time))
        .filter((m) => m !== null);
      if (validMins.length > 0) {
        scrollMin = Math.min(...validMins);
      }
    }

    if (scrollMin !== null) {
      requestAnimationFrame(() => {
        if (!bodyRef.current) return;
        const scrollTarget = Math.max(0, (scrollMin / 60 - 1) * HOUR_PX);
        bodyRef.current.scrollTop = scrollTarget;
      });
    }
  }, [timed.length, initialScrollMinutes]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Layout clashing events side-by-side ────────────────────────────────────
  const renderedTimed = timed
    .map((ev) => {
      const rawStartMin = toMinutes(ev.drawStart || ev.time);
      const rawEndMin = ev.drawEnd
        ? toMinutes(ev.drawEnd)
        : ev.endTime
          ? toMinutes(ev.endTime)
          : rawStartMin + 60;

      // Enforce a minimum layout duration (e.g. 75 mins) to ensure
      // there is enough room for text details without overlap.
      const MIN_DURATION = 75;
      const startMin = rawStartMin;
      const endMin = Math.max(rawEndMin, rawStartMin + MIN_DURATION);

      return { ...ev, startMin, endMin, rawStartMin, rawEndMin };
    })
    .sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);

  const layoutEvents = [];
  let currentGroup = [];
  let currentGroupEnd = 0;

  const processGroup = (group) => {
    const columns = [];
    group.forEach((ev) => {
      let placed = false;
      for (let i = 0; i < columns.length; i++) {
        const lastInCol = columns[i][columns[i].length - 1];
        if (lastInCol.endMin <= ev.startMin) {
          columns[i].push(ev);
          ev.colIndex = i;
          placed = true;
          break;
        }
      }
      if (!placed) {
        ev.colIndex = columns.length;
        columns.push([ev]);
      }
    });
    const numColumns = columns.length;
    group.forEach((ev) => {
      ev.numColumns = numColumns;
      layoutEvents.push(ev);
    });
  };

  renderedTimed.forEach((ev) => {
    if (currentGroup.length === 0) {
      currentGroup.push(ev);
      currentGroupEnd = ev.endMin;
    } else if (ev.startMin < currentGroupEnd) {
      currentGroup.push(ev);
      currentGroupEnd = Math.max(currentGroupEnd, ev.endMin);
    } else {
      processGroup(currentGroup);
      currentGroup = [ev];
      currentGroupEnd = ev.endMin;
    }
  });
  if (currentGroup.length > 0) processGroup(currentGroup);

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        />
        <div className="relative z-10 w-full sm:max-w-lg max-h-[90vh] flex flex-col rounded-t-2xl sm:rounded-2xl bg-white shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between bg-slate-900 px-5 py-4 shrink-0">
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-widest font-semibold">
                Schedule
              </p>
              <h2 className="text-white font-semibold text-lg leading-tight mt-0.5">
                {label}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition"
            >
              &#x2715;
            </button>
          </div>

          {/* Body */}
          <div ref={bodyRef} className="flex-1 overflow-y-auto">
            {/* All Day strip */}
            {allDay.length > 0 && (
              <div className="border-b border-slate-100 bg-slate-50 px-4 pt-3 pb-3">
                <p className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-400">
                  All Day
                </p>
                <div className="space-y-1.5">
                  {allDay.map((ev) => {
                    const s = TYPE_STYLE[ev.type] || TYPE_STYLE.custom;
                    return (
                      <div
                        key={ev.id}
                        onClick={() => ev.removable && setEditingEvent(ev.raw)}
                        className={`flex items-center gap-2 rounded-lg border ${s.border} ${s.light} px-3 py-1.5 ${ev.removable ? "cursor-pointer hover:shadow-sm" : ""}`}
                      >
                        <span
                          className={`h-2 w-2 shrink-0 rounded-full ${s.solid}`}
                        />
                        <div className="min-w-0">
                          <p
                            className={`text-sm font-semibold ${s.text} truncate`}
                          >
                            {ev.title}
                          </p>
                          {ev.subtitle && (
                            <p className="text-xs text-slate-500 truncate">
                              {ev.subtitle}
                            </p>
                          )}
                        </div>
                        {ev.removable && (
                          <div className="ml-auto flex items-center gap-1.5">
                            <div className="flex h-6 w-6 items-center justify-center rounded bg-slate-200/50 text-slate-500 transition text-xs">
                              <i className="fa fa-pencil" />
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onRemoveCustom(ev.id);
                              }}
                              className="shrink-0 flex h-6 w-6 items-center justify-center rounded text-red-400 hover:bg-red-100 hover:text-red-600 transition text-sm"
                            >
                              <i className="fa fa-trash" />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Visual Timeline — always shown */}
            <div className="px-0 pt-2 pb-4">
              <div className="relative" style={{ height: totalHeight }}>
                {/* Hour grid lines + labels */}
                {hours.map((h) => {
                  const top = (h - gridStartHour) * HOUR_PX;
                  const label12 =
                    h === 0
                      ? "12 AM"
                      : h < 12
                        ? `${h} AM`
                        : h === 12
                          ? "12 PM"
                          : `${h - 12} PM`;
                  return (
                    <div
                      key={h}
                      className="absolute left-0 right-0 flex items-start"
                      style={{ top }}
                    >
                      <span className="w-16 shrink-0 pr-3 text-right text-xs font-mono text-slate-400 leading-none -mt-px select-none">
                        {label12}
                      </span>
                      <div className="flex-1 border-t border-slate-100" />
                    </div>
                  );
                })}

                {/* Event blocks */}
                <div className="absolute top-0 left-16 right-4 bottom-0">
                  {layoutEvents.map((ev) => {
                    const s = TYPE_STYLE[ev.type] || TYPE_STYLE.custom;
                    const topPx = (ev.rawStartMin / 60) * HOUR_PX;
                    const heightPx = ((ev.endMin - ev.startMin) / 60) * HOUR_PX;

                    return (
                      <div
                        key={ev.id}
                        onClick={() => ev.removable && setEditingEvent(ev.raw)}
                        className={`absolute rounded-lg border ${s.border} ${s.light} group overflow-hidden ${ev.removable ? "cursor-pointer hover:ring-1 hover:ring-slate-300" : ""}`}
                        style={{
                          top: topPx,
                          height: heightPx,
                          left: `${(ev.colIndex / ev.numColumns) * 100}%`,
                          width: `${100 / ev.numColumns}%`,
                          zIndex: 10,
                        }}
                      >
                        {/* Colour bar */}
                        <div
                          className={`absolute left-0 top-0 bottom-0 w-1 ${s.solid} rounded-l-lg z-20`}
                        />

                        {/* Content Container */}
                        <div className="h-full w-full">
                          <div className="pl-3 pr-2 pt-1.5 pb-1.5 min-w-0">
                            <p
                              className={`text-sm font-bold leading-tight truncate ${s.text}`}
                            >
                              {ev.type === "hall" && ev.fullName
                                ? ev.fullName
                                : ev.title}
                            </p>

                            {ev.type === "hall" && (
                              <div className="mt-0.5">
                                <p className="text-xs font-semibold text-slate-700 truncate tracking-tight">
                                  {ev.occasion} • {ev.guests} guests
                                </p>
                              </div>
                            )}

                            <p className="text-xs text-slate-600 mt-0.5 leading-tight truncate">
                              {ev.spillPrev && "(prev day) "}
                              {fmt12(ev.time)}
                              {ev.endTime ? ` – ${fmt12(ev.endTime)}` : ""}
                              {ev.spillNext && " (next day)"}
                            </p>

                            {heightPx > HOUR_PX * 0.8 && ev.subtitle && (
                              <p className="text-xs text-slate-600 mt-0.5 line-clamp-2 italic">
                                {ev.subtitle}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Edit Pencil Icon (Desktop only hover) */}
                        {ev.removable && (
                          <div className="absolute top-1 right-8 flex h-6 w-6 items-center justify-center rounded bg-slate-100 text-slate-500 opacity-0 group-hover:opacity-100 transition shadow-sm border border-slate-200">
                            <i className="fa fa-pencil text-xs" />
                          </div>
                        )}

                        {/* Remove button for custom events */}
                        {ev.removable && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onRemoveCustom(ev.id);
                            }}
                            className="absolute top-1 right-1 flex h-6 w-6 items-center justify-center rounded bg-red-100 text-red-400 hover:bg-red-200 hover:text-red-700 transition text-sm opacity-0 group-hover:opacity-100"
                            title="Remove"
                          >
                            <i className="fa fa-trash" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Empty state */}
            {!hasAny && !showAddModal && (
              <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                <span className="text-4xl mb-3">&#x1F4EB;</span>
                <p className="text-sm">No events on this day.</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="shrink-0 border-t border-slate-100 px-5 py-3">
            {isPastDate ? (
              <p className="text-center text-xs text-slate-400 py-1">
                &#x1F512; Cannot add events to a past date
              </p>
            ) : (
              <button
                onClick={() => setShowAddModal(true)}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-amber-500 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-amber-600 active:scale-95 transition"
              >
                <span className="text-base">&#xff0b;</span>
                Add Event
              </button>
            )}
          </div>
        </div>

        {/* Add Event Modal — rendered above DayPanel */}
        {showAddModal && (
          <AddEventModal
            dateStr={dateStr}
            dateLabel={label}
            onAdd={handleAdd}
            onClose={() => setShowAddModal(false)}
          />
        )}

        {/* Edit Event Modal */}
        {editingEvent && (
          <AddEventModal
            dateStr={dateStr}
            dateLabel={label}
            initialData={editingEvent}
            onAdd={handleEdit}
            onClose={() => setEditingEvent(null)}
          />
        )}
      </div>
    </>
  );
}

// ─── Main CalendarTab ─────────────────────────────────────────────────────────

const CalendarTab = () => {
  const ctx = useContext(CalendarContext);

  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth()); // 0-indexed

  const [hallRentals, setHallRentals] = useState([]); // flat array
  const [hallByDate, setHallByDate] = useState({}); // { "YYYY-MM-DD": [rental] }
  const [loadingHall, setLoadingHall] = useState(false);

  const [customEvents, setCustomEvents] = useState({}); // { "YYYY-MM-DD": [event] } — from DB

  const [selectedDate, setSelectedDate] = useState(null); // "YYYY-MM-DD"
  const [scrollMinutes, setScrollMinutes] = useState(null); // time to scroll to in DayPanel
  const [eventToDelete, setEventToDelete] = useState(null); // { dateStr, id }

  // ── Navigate to target date from CalendarContext ──────────────────────────
  useEffect(() => {
    if (ctx?.calendarTargetDate) {
      const d = parseLocalDate(ctx.calendarTargetDate);
      if (d) {
        setViewYear(d.getFullYear());
        setViewMonth(d.getMonth());
        setSelectedDate(ctx.calendarTargetDate);
      }
      ctx.setCalendarTargetDate(null);
    }
  }, [ctx]);

  // ── Fetch Hall Rentals ────────────────────────────────────────────────────
  const fetchHallRentals = useCallback(async () => {
    setLoadingHall(true);
    try {
      const data = await adminApiRequest({ script: "get-hall-rentals" });
      if (data.success) {
        setHallRentals(data.data || []);
      }
    } catch (err) {
      console.error("Calendar: failed to fetch hall rentals", err);
    } finally {
      setLoadingHall(false);
    }
  }, []);

  useEffect(() => {
    fetchHallRentals();
  }, [fetchHallRentals]);

  // Index rentals by event_date
  useEffect(() => {
    const map = {};
    hallRentals.forEach((r) => {
      if (!r.event_date) return;
      const key = r.event_date.split("T")[0].substring(0, 10);
      if (!map[key]) map[key] = [];

      if (r.start_time && r.end_time && r.end_time < r.start_time) {
        // Spills to next day
        const r1 = { ...r, drawEnd: "24:00", spillNext: true };
        map[key].push(r1);

        const d = new Date(key);
        d.setDate(d.getDate() + 1);
        const nextKey = toYMD(d);
        if (!map[nextKey]) map[nextKey] = [];

        const r2 = {
          ...r,
          id: r.id + "-spill",
          drawStart: "00:00",
          spillPrev: true,
        };
        map[nextKey].push(r2);
      } else {
        map[key].push(r);
      }
    });
    setHallByDate(map);
  }, [hallRentals]);

  // ── Fetch & sync custom events (DB) ─────────────────────────────────────────
  const fetchCustomEvents = useCallback(async () => {
    try {
      const data = await adminApiRequest({ script: "get-calendar-events" });
      if (data.success) {
        const map = {};
        (data.data || []).forEach((ev) => {
          const key = (ev.event_date || "").substring(0, 10);
          if (!key) return;
          if (!map[key]) map[key] = [];

          const eventObj = {
            id: String(ev.id),
            title: ev.title,
            time: ev.start_time || null,
            endTime: ev.end_time || null,
            description: ev.description || "",
            type: ev.type || ev.event_type || "task",
            // New details
            fullName: ev.full_name || "",
            contact: ev.contact_number || "",
            email: ev.email || "",
            guests: ev.guests || "",
            streetAddress: ev.street_address || "",
            city: ev.city || "",
            postcode: ev.postcode || "",
            country: ev.country || "",
            specialRequirements: ev.special_requirements || "",
            charityEvent: ev.charity_event || "No",
            occasion: ev.occasion || "",
            notes: ev.notes || "",
            agreedTerms: Number(ev.agreed_terms) === 1,
          };

          if (ev.start_time && ev.end_time && ev.end_time < ev.start_time) {
            // Spills to next day
            eventObj.drawEnd = "24:00";
            eventObj.spillNext = true;
            map[key].push(eventObj);

            const d = new Date(key);
            d.setDate(d.getDate() + 1);
            const nextKey = toYMD(d);
            if (!map[nextKey]) map[nextKey] = [];

            map[nextKey].push({
              ...eventObj,
              id: eventObj.id + "-spill",
              drawStart: "00:00",
              drawEnd: undefined,
              spillNext: undefined,
              spillPrev: true,
            });
          } else {
            map[key].push(eventObj);
          }
        });
        setCustomEvents(map);
      }
    } catch (err) {
      console.error("Calendar: failed to fetch custom events", err);
    }
  }, []);

  useEffect(() => {
    fetchCustomEvents();
  }, [fetchCustomEvents]);

  const addCustomEvent = async (dateStr, event) => {
    try {
      const isUpdate = !isNaN(event.id) && String(event.id).length < 10;

      const payload = {
        id: isUpdate ? event.id : undefined,
        event_date: dateStr,
        title: event.title,
        start_time: event.time || "",
        end_time: event.endTime || "",
        description: event.description || "",
        type: event.type || "task",
        // New fields
        fullName: event.fullName,
        contact: event.contact,
        email: event.email,
        guests: event.guests,
        streetAddress: event.streetAddress,
        city: event.city,
        postcode: event.postcode,
        country: event.country,
        specialRequirements: event.specialRequirements,
        charityEvent: event.charityEvent,
        notes: event.notes,
        agreedTerms: event.agreedTerms ? 1 : 0,
        occasion: event.occasion,
      };

      const res = await adminApiRequest({
        script: "save-calendar-event",
        data: payload,
      });
      if (res.success) await fetchCustomEvents();
    } catch (err) {
      console.error("Calendar: failed to save event", err);
    }
  };

  const removeCustomEvent = async (dateStr, eventId) => {
    const realId = String(eventId).replace("-spill", "");

    try {
      const res = await adminApiRequest({
        script: "delete-calendar-event",
        data: { id: realId },
      });
      if (res.success) {
        await fetchCustomEvents();
      }
    } catch (err) {
      console.error("Calendar: failed to delete event", err);
    }
  };

  // ── Calendar grid builder ─────────────────────────────────────────────────

  const firstDay = (new Date(viewYear, viewMonth, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const todayStr = toYMD(today);
  const lastSundayStr = getLastSundayOfMonth(viewYear, viewMonth);

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null); // padding
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push(new Date(viewYear, viewMonth, d));
  }
  // pad to full rows
  while (cells.length % 7 !== 0) cells.push(null);

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewYear((y) => y - 1);
      setViewMonth(11);
    } else setViewMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewYear((y) => y + 1);
      setViewMonth(0);
    } else setViewMonth((m) => m + 1);
  };
  const goToday = () => {
    setViewYear(today.getFullYear());
    setViewMonth(today.getMonth());
  };

  // ── Selected day events ───────────────────────────────────────────────────
  const selectedHall = selectedDate ? hallByDate[selectedDate] || [] : [];
  const selectedCustom = selectedDate ? customEvents[selectedDate] || [] : [];

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* ── Header ── */}
      <div className="mb-4 flex shrink-0 flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800 tracking-tight">
            Calendar
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Hall rentals &amp; custom events
          </p>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600">
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-2.5 w-2.5 rounded-full bg-purple-600" />
            Hall Rental
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-2.5 w-2.5 rounded-full bg-teal-600" />
            Buffet Day
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-2.5 w-2.5 rounded-full bg-amber-600" />
            Task
          </span>
        </div>
      </div>

      {/* ── Month Nav ── */}
      <div className="mb-4 flex shrink-0 items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <button
            onClick={prevMonth}
            className="flex h-9 w-9 items-center justify-center rounded-xl bg-white border border-slate-200 text-slate-600 shadow-sm hover:bg-slate-50 hover:text-slate-900 transition active:scale-95"
          >
            ‹
          </button>
          <button
            onClick={nextMonth}
            className="flex h-9 w-9 items-center justify-center rounded-xl bg-white border border-slate-200 text-slate-600 shadow-sm hover:bg-slate-50 hover:text-slate-900 transition active:scale-95"
          >
            ›
          </button>
        </div>

        <h2 className="text-lg font-bold text-slate-800">
          {MONTHS[viewMonth]} {viewYear}
        </h2>

        <button
          onClick={goToday}
          className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 transition active:scale-95"
        >
          Today
        </button>
      </div>

      {/* ── Loading bar ── */}
      {loadingHall && (
        <div className="mb-3 h-1 w-full overflow-hidden rounded-full bg-slate-200">
          <div className="h-full animate-pulse bg-sky-500 rounded-full w-2/3" />
        </div>
      )}

      {/* ── Calendar Grid ── */}
      <div className="flex-1 overflow-auto">
        <div className="min-w-[480px]">
          {/* Day-of-week headers */}
          <div className="grid grid-cols-7 mb-1">
            {DAYS.map((d) => (
              <div
                key={d}
                className="py-2 text-center text-xs font-semibold uppercase tracking-widest text-slate-500"
              >
                {d}
              </div>
            ))}
          </div>

          {/* Cells */}
          <div className="grid grid-cols-7 gap-1">
            {cells.map((date, idx) => {
              if (!date) {
                return <div key={`pad-${idx}`} className="rounded-xl" />;
              }

              const ymd = toYMD(date);
              const isToday = ymd === todayStr;
              const isSelected = ymd === selectedDate;
              const isBuffetDay = ymd === lastSundayStr;

              const dayLegacyHall = hallByDate[ymd] || [];
              const dayCustom = customEvents[ymd] || [];

              const customHallCount = dayCustom.filter(
                (ev) => ev.type === "hall",
              ).length;
              const totalHallCount = dayLegacyHall.length + customHallCount;
              const otherCustom = dayCustom.filter((ev) => ev.type !== "hall");

              const hasEvents =
                totalHallCount > 0 || otherCustom.length > 0 || isBuffetDay;
              const isPast =
                date <
                new Date(
                  today.getFullYear(),
                  today.getMonth(),
                  today.getDate(),
                );

              const handleCellClick = () => {
                setScrollMinutes(null);
                setSelectedDate(isSelected ? null : ymd);
              };

              const handleHallPillClick = () => {
                const allDayRentals = [
                  ...dayLegacyHall.map((r) => r.drawStart || r.start_time),
                  ...dayCustom
                    .filter((ev) => ev.type === "hall")
                    .map((ev) => ev.drawStart || ev.time),
                ].filter(Boolean);

                if (allDayRentals.length > 0) {
                  const mins = allDayRentals.map(toMinutes);
                  setScrollMinutes(Math.min(...mins));
                } else {
                  setScrollMinutes(null);
                }
                setSelectedDate(ymd);
              };

              const handlePillClick = (time, targetYmd) => {
                if (time !== null && time !== undefined) {
                  setScrollMinutes(toMinutes(time));
                } else {
                  setScrollMinutes(null);
                }
                setSelectedDate(targetYmd);
              };

              return (
                <button
                  key={ymd}
                  onClick={handleCellClick}
                  className={`
                    relative flex min-h-[72px] sm:min-h-[88px] flex-col items-start rounded-xl border p-2 text-left transition
                    ${
                      isSelected
                        ? "border-slate-800 bg-slate-900 text-white shadow-lg ring-2 ring-slate-700"
                        : isToday
                          ? "border-sky-300 bg-sky-50 shadow-sm"
                          : isPast
                            ? "border-slate-200 bg-slate-50/80 opacity-90"
                            : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm"
                    }
                  `}
                >
                  <span
                    className={`
                      mb-1 flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold
                      ${isToday && !isSelected ? "bg-sky-500 text-white" : ""}
                      ${isSelected ? "text-white" : isToday ? "" : "text-slate-700"}
                    `}
                  >
                    {date.getDate()}
                  </span>

                  <div className="flex w-full flex-col gap-0.5 overflow-hidden">
                    {isBuffetDay && <EventPill type="buffet" label="Buffet" />}

                    {totalHallCount > 0 && (
                      <EventPill
                        type="hall"
                        label={`${totalHallCount} Hall Rental${totalHallCount > 1 ? "s" : ""}`}
                        onClick={handleHallPillClick}
                      />
                    )}

                    {otherCustom.map((ev, i) => (
                      <EventPill
                        key={`custom-${ev.id || i}`}
                        type={ev.type || "task"}
                        label={ev.title}
                        onClick={() =>
                          handlePillClick(ev.drawStart || ev.time, ymd)
                        }
                      />
                    ))}
                  </div>

                  {hasEvents && !isSelected && (
                    <span className="absolute right-2 top-2 h-1.5 w-1.5 rounded-full bg-pink-500" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Day Detail Panel ── */}
      {selectedDate && (
        <DayPanel
          dateStr={selectedDate}
          hallEvents={selectedHall}
          customEvents={selectedCustom}
          isBuffetDay={selectedDate === lastSundayStr}
          onClose={() => {
            setSelectedDate(null);
            setScrollMinutes(null);
          }}
          onAddCustom={(ev) => addCustomEvent(selectedDate, ev)}
          onEditCustom={(ev) => addCustomEvent(selectedDate, ev)}
          onRemoveCustom={(id) =>
            setEventToDelete({ dateStr: selectedDate, id })
          }
          initialScrollMinutes={scrollMinutes}
        />
      )}

      {/* ── Delete Confirm Modal ── */}
      {eventToDelete && (
        <DeleteConfirmModal
          onCancel={() => setEventToDelete(null)}
          onConfirm={async () => {
            await removeCustomEvent(eventToDelete.dateStr, eventToDelete.id);
            setEventToDelete(null);
          }}
        />
      )}
    </div>
  );
};

export default CalendarTab;
